import { useEffect, useRef, useState } from 'react';

import bgMockLG from '@/assets/img_mock_form_decidir-lg.png';
import bgMockSM from '@/assets/img_mock_form_decidir.png';
import { Button, Loader } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { GDTApiErrors } from '@/types';
import { CardKey, CONF_TARJETAS, URLs } from '@/utils';
import { cn, formatNumberToLocale } from '@/utils/functions';

type Props = {
  amount: number;
  cardType: CardKey;
  productId?: number;
  installments?: number;
  onFinish: (success: boolean) => void;
  labelTokenTarjeta?: string;
  tokenTarjeta?: string;
};

// const testButton = true;

/**
 * diagrama de secuencia en
 * https://agea.atlassian.net/wiki/spaces/GDT/pages/2766602270/Form+de+Pago+responsive
 *
 * https://bitbucket.org/ageabit/grandt-a/src/master/GranDT-Mobile/util/mermaid/seq_gwPagos.mermaid
 *
 * @param param0
 * @returns
 */
export const FormDecidir = ({
  amount,
  cardType,
  productId,
  installments,
  labelTokenTarjeta,
  tokenTarjeta,
  onFinish,
}: Props) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [state, setState] = useState<{
    error: boolean | string;
    isReturnFlow: boolean;
    loaded: boolean;
  }>({
    error: false,
    loaded: false,
    isReturnFlow: false,
  });

  const { isMobile } = useIsDesktop();

  const registerCallbacks = (onFinish: Props['onFinish']) => {
    const eventListener = (event: MessageEvent<any>): void => {
      // if (event.origin !== location.origin) return;
      const eventData = event.data as GDTApiErrors & { type: string };

      if (eventData.type !== 'paymentForm') return;
      setState({
        loaded: true,
        error: !eventData.estado,
        isReturnFlow: true,
      });
      onFinish(eventData.estado);
    };
    window.addEventListener('message', eventListener, false);
    return eventListener;
  };

  useEffect(() => {
    if (iframeRef.current) {
      const params = {
        canalVisa: CONF_TARJETAS[cardType].key,
        monto: amount + '',
        labelTokenTarjeta: String(labelTokenTarjeta),
        tokenTarjeta: String(tokenTarjeta),
        tipoProducto: productId ? productId + '' : '',
        cantidadCuotas: (installments ?? 1) + '',
      };
      const queryString = new URLSearchParams(params).toString();
      const url = `${URLs.MI_CUENTA.FORM_PAGO}?${queryString}`;
      try {
        // Create a form element
        const form: HTMLFormElement = document.createElement('form');
        form.method = 'POST';
        form.action = url;
        // Append the form to the iframe's document body
        if (iframeRef.current.contentDocument) {
          iframeRef.current.contentDocument.body.appendChild(form);
          // Submit the form
          form.submit();
        } else {
          setTimeout(() => {
            setState({
              isReturnFlow: false,
              loaded: true,
              error: true,
            });
          }, 0);
        }
      } catch (err) {
        setTimeout(() => {
          setState({
            isReturnFlow: false,
            loaded: true,
            error: true,
          });
        }, 0);
        console.error(err);
      }
    }
    const eventListener = registerCallbacks(onFinish);
    return () => {
      window.removeEventListener('message', eventListener);
    };
  }, [
    amount,
    cardType,
    productId,
    installments,
    onFinish,
    labelTokenTarjeta,
    tokenTarjeta,
  ]);

  if (state.error && !state.isReturnFlow) {
    return (
      <p className="text-center p-4 w-full">
        Tu transacción no pudo realizarse. Intentalo de nuevo o comunicate al{' '}
        <Button href="tel:+5491152891740" type="link" variant="text">
          5289-1740
        </Button>
      </p>
    );
  }

  return (
    <>
      <Loader isVisible={!state.loaded} timeout={false} />
      <div className="bg-gdt-gray-7 relative h-[683px] w-full">
        <div className="px-4 h-full">
          {!state.isReturnFlow ? (
            <>
              <iframe
                ref={iframeRef}
                allowFullScreen={false}
                className="form-decidir w-full h-full"
                id="if_payment"
                name="if_payment"
                title="Formulario de pago"
                onLoad={() =>
                  setState({
                    ...state,
                    loaded: true,
                  })
                }
              />

              <div
                className={cn(
                  'hidden absolute top-0 max-w-[425px] w-[calc(100%-30px)] text-gdt-blue rounded-lg px-4 h-12 items-center justify-between bg-gdt-blue-xl2 mt-3 font-bold text-sm left-1/2 -translate-x-1/2 translate-y-[472px]',
                  {
                    flex: state.loaded,
                  }
                )}
              >
                <p>Total a pagar</p>
                <span>${formatNumberToLocale(amount)}</span>
              </div>
            </>
          ) : (
            <figure className="max-w-[425px] mx-auto">
              <img
                alt="Imagen de formulario de pago"
                className="w-full"
                src={isMobile ? bgMockSM : bgMockLG}
              />
            </figure>
          )}
        </div>
      </div>
    </>
  );
};

// {/* {testButton && (
//   <button value="test ok" onClick={testOk}>
//     {' '}
//     test ok
//   </button>
// )} */}
// export function testFinPago(esOk: boolean) {
//   const ifRef = document.querySelector('#if_payment') as HTMLIFrameElement;
//   if (ifRef.contentWindow) {
//     const urlBase = '/form-pagos/gw-form-response-sync.html?';
//     ifRef.contentWindow.location.href = esOk
//       ? `${urlBase}estado=ACREDITADO&idTransaccionCliente=B123456789`
//       : `${urlBase}estado=ERROR`; // ERROR o CANCELADO
//   }
// }

// function testOk() {
//   testFinPago(true);
// }
